/**
 * Configurable multi-panel qEEG trend set (amplitude/aEEG, envelope,
 * CDSA, rhythmicity, asymmetry, burst-suppression, alpha/delta variability).
 */

import type { QEEGTrendSample, QEEGTrendType } from "./qeegTrendTransforms";

export const QEEG_PANEL_CONFIG_SCHEMA_VERSION = "qeeg-panel-config-v1.0.0";

export type QeegPanelId =
  | "amplitude_aeeg"
  | "envelope"
  | "cdsa_spectrogram"
  | "rhythmicity"
  | "asymmetry"
  | "burst_suppression"
  | "alpha_delta_variability";

export type QeegPanelDefinition = {
  id: QeegPanelId;
  label: string;
  trendTypes: QEEGTrendType[];
  defaultEnabled: boolean;
};

export const QEEG_PANEL_CATALOG: QeegPanelDefinition[] = [
  {
    id: "amplitude_aeeg",
    label: "Amplitude / aEEG",
    trendTypes: ["aeeg", "amplitude"],
    defaultEnabled: true,
  },
  {
    id: "envelope",
    label: "Envelope",
    trendTypes: ["amplitude"],
    defaultEnabled: false,
  },
  {
    id: "cdsa_spectrogram",
    label: "CDSA / Spectrogram",
    trendTypes: ["spectrogram"],
    defaultEnabled: true,
  },
  {
    id: "rhythmicity",
    label: "Rhythmicity",
    trendTypes: ["rhythmicity"],
    defaultEnabled: true,
  },
  {
    id: "asymmetry",
    label: "Asymmetry",
    trendTypes: ["asymmetry"],
    defaultEnabled: true,
  },
  {
    id: "burst_suppression",
    label: "Burst-suppression",
    trendTypes: ["suppression"],
    defaultEnabled: false,
  },
  {
    id: "alpha_delta_variability",
    label: "Alpha/delta variability",
    trendTypes: ["ratio", "bandpower"],
    defaultEnabled: true,
  },
];

export type QeegTrendPanelConfig = {
  schemaVersion: string;
  enabledPanelIds: QeegPanelId[];
  presetName: string | null;
};

export function createDefaultTrendPanelConfig(
  presetName: string | null = "icu_default",
): QeegTrendPanelConfig {
  return {
    schemaVersion: QEEG_PANEL_CONFIG_SCHEMA_VERSION,
    enabledPanelIds: QEEG_PANEL_CATALOG.filter((p) => p.defaultEnabled).map(
      (p) => p.id,
    ),
    presetName,
  };
}

export function toggleTrendPanel(
  config: QeegTrendPanelConfig,
  panelId: QeegPanelId,
  enabled: boolean,
): QeegTrendPanelConfig {
  const set = new Set(config.enabledPanelIds);
  if (enabled) set.add(panelId);
  else set.delete(panelId);
  return {
    ...config,
    enabledPanelIds: QEEG_PANEL_CATALOG.map((p) => p.id).filter((id) =>
      set.has(id),
    ),
  };
}

/**
 * Alpha/delta variability: coefficient of variation of alpha/delta ratio
 * across sliding epochs.
 */
export function computeAlphaDeltaVariability(
  ratioSamples: QEEGTrendSample[],
  windowSize = 6,
): QEEGTrendSample[] {
  if (ratioSamples.length === 0) return [];
  const out: QEEGTrendSample[] = [];
  for (let i = 0; i < ratioSamples.length; i += 1) {
    const start = Math.max(0, i - windowSize + 1);
    const slice = ratioSamples.slice(start, i + 1).map((s) => s.value);
    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
    const variance =
      slice.reduce((a, b) => a + (b - mean) ** 2, 0) / slice.length;
    const std = Math.sqrt(variance);
    const cv = mean > 1e-9 ? std / mean : 0;
    out.push({
      timeSeconds: ratioSamples[i].timeSeconds,
      value: cv,
      secondaryValue: mean,
    });
  }
  return out;
}
